import { OtsFileTypeIcon, type OtsFileKind, otsFileKindLabel } from "../components/OtsFileTypeIcon";
import { OtsTooltip } from "../components/OtsTooltip";
import type { ActivityAttachment } from "./driver-activity-view-model";

export type ActivityAttachmentsBadgeProps = {
  attachments: ActivityAttachment[];
  /** Opens the attachments; without it the badge is a plain indicator. */
  onOpen?: () => void;
};

const MAX_THUMBNAILS = 4;

/** How many of each kind, in a stable order: pictures first. */
export const countAttachmentKinds = (attachments: ActivityAttachment[]): [OtsFileKind, number][] => {
  const counts = new Map<OtsFileKind, number>();
  for (const { kind } of attachments) counts.set(kind, (counts.get(kind) ?? 0) + 1);
  return [...counts].sort(([a], [b]) => Number(b === "image") - Number(a === "image"));
};

const describe = (kinds: [OtsFileKind, number][]) =>
  kinds.map(([kind, count]) => `${count} ${otsFileKindLabel(kind).toLowerCase()}${count === 1 ? "" : "s"}`).join(", ");

/** A ticket's attachments as one icon per file type with a count: pictures on hover, everything on click. */
export const ActivityAttachmentsBadge = ({ attachments, onOpen }: ActivityAttachmentsBadgeProps) => {
  if (!attachments.length) return null;
  const kinds = countAttachmentKinds(attachments);
  const label = describe(kinds);
  const pictures = attachments.filter((attachment) => attachment.kind === "image");
  const files = attachments.filter((attachment) => attachment.kind !== "image");

  const content = kinds.map(([kind, count]) => (
    <span key={kind} className="inline-flex items-center gap-px">
      <OtsFileTypeIcon kind={kind} className="size-4"/>
      <span aria-hidden="true" className="text-[10px] font-bold leading-none tabular-nums">{count}</span>
    </span>
  ));
  const classes = "inline-flex items-center gap-1 rounded px-0.5 text-[var(--ots-text-muted)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]";

  return (
    <OtsTooltip
      density="compact"
      panelClassName="!max-w-xs"
      content={<div className="space-y-1.5">
        <p className="font-semibold">{label}{onOpen ? " · click to open" : ""}</p>
        {pictures.length > 0 && <div className="flex gap-1">
          {pictures.slice(0, MAX_THUMBNAILS).map((picture) => <img key={picture.id} src={picture.url} alt="" className="size-14 rounded border border-[var(--ots-outline-variant)] object-cover"/>)}
          {pictures.length > MAX_THUMBNAILS && <span className="flex size-14 items-center justify-center rounded bg-[var(--ots-surface-variant)] font-semibold">+{pictures.length - MAX_THUMBNAILS}</span>}
        </div>}
        {files.map((file) => <p key={file.id} className="flex items-center gap-1 truncate"><OtsFileTypeIcon kind={file.kind} className="size-3.5 shrink-0"/>{file.name}</p>)}
      </div>}>
      {onOpen
        ? <button
          type="button"
          aria-label={`Open attachments: ${label}`}
          onClick={(event) => {
            // The row's own click opens the inspector; this is its own action.
            event.stopPropagation();
            onOpen();
          }}
          className={[classes, "cursor-pointer hover:bg-[var(--ots-primary-container)]"].join(" ")}>
          {content}
        </button>
        : <span tabIndex={0} role="img" aria-label={label} className={classes}>{content}</span>}
    </OtsTooltip>
  );
};
